import React, { useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setDialog } from 'src/features/dialog/dialogSlice';
import {
  useGetTenantInformationQuery,
  useSendTenantTestMailMutation,
  useUpdateTenantMailSettingsMutation,
} from 'src/features/tenants/tenantsApiSlice';
import { selectCurrentUserData } from 'src/features/auth/authSlice';
import MailTemplatePageLayout from './MailTemplatePageLayout';

const tokens = [
  {
    value: '{{formalGreeting}}',
    label: 'Formale Anrede',
    desc: 'Automatische Anrede basierend auf dem Titel: Herr → Sehr geehrter Herr [Nachname], Frau → Sehr geehrte Frau [Nachname], kein Titel → Guten Tag [Vollständiger Name].',
  },
  {
    value: '{{memberFirstName}}',
    label: 'Vorname',
    desc: 'Vorname des Mitglieds.',
  },
  {
    value: '{{memberLastName}}',
    label: 'Nachname',
    desc: 'Nachname des Mitglieds.',
  },
  {
    value: '{{memberFullName}}',
    label: 'Vollständiger Name',
    desc: 'Vor- und Nachname des Mitglieds.',
  },
  {
    value: '{{terminationDate}}',
    label: 'Kündigungsdatum',
    desc: 'Das Datum, an dem die Kündigung eingegangen ist.',
  },
  {
    value: '{{lastMonth}}',
    label: 'Letzter Beitragsmonat',
    desc: 'Der letzte Monat, für den Mitgliedsbeiträge anfallen.',
  },
  {
    value: '{{contractEndDate}}',
    label: 'Vertragsende',
    desc: 'Das Datum, an dem der Vertrag offiziell endet.',
  },
  {
    value: '{{noticePeriod}}',
    label: 'Kündigungsfrist (Monate)',
    desc: 'Die vertraglich festgelegte Kündigungsfrist in Monaten.',
  },
  {
    value: '{{contractDate}}',
    label: 'Vertragsbeginn',
    desc: 'Das Datum des Vertragsabschlusses.',
  },
  {
    value: '{{contractDuration}}',
    label: 'Mindestlaufzeit (Monate)',
    desc: 'Die Mindestlaufzeit des Vertrags in Monaten.',
  },
  {
    value: '{{contractExtension}}',
    label: 'Verlängerung (Monate)',
    desc: 'Automatische Vertragsverlängerung in Monaten nach Ablauf der Mindestlaufzeit.',
  },
  {
    value: '{{contractOwner}}',
    label: 'Vertragsinhaber',
    desc: 'Name der Person, die den Vertrag abgeschlossen hat.',
  },
  {
    value: '{{contractEmail}}',
    label: 'E-Mail-Adresse',
    desc: 'Die im Vertrag hinterlegte E-Mail-Adresse.',
  },
  {
    value: '{{tenantName}}',
    label: 'Vereinsname',
    desc: 'Name Ihres Vereins.',
  },
  {
    value: '{{today}}',
    label: 'Heutiges Datum',
    desc: 'Das aktuelle Datum zum Zeitpunkt des E-Mail-Versands.',
  },
];

const MailTerminationTemplateSettings = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const currentUserData = useSelector(selectCurrentUserData);
  const { data: tenantInfo, isLoading } = useGetTenantInformationQuery();
  const [updateTenantMailSettings, { isLoading: isSaving }] =
    useUpdateTenantMailSettingsMutation();
  const [sendTenantTestMail, { isLoading: isTesting }] =
    useSendTenantTestMailMutation();

  const defaultValues = useMemo(
    () => ({
      terminationSubject:
        tenantInfo?.settings?.mail?.terminationSubject || 'Kündigung bestätigt',
      terminationTemplate:
        tenantInfo?.settings?.mail?.terminationTemplate ||
        '{{formalGreeting}},\n\nhiermit bestätigen wir die Kündigung für {{memberFullName}}.\nKündigungstermin: {{terminationDate}}\nLetzter Beitragsmonat: {{lastMonth}}\nVertragsende: {{contractEndDate}}\nKündigungsfrist: {{noticePeriod}} Monate\n\nVertragsdaten:\nVertragsbeginn: {{contractDate}}\nMindestlaufzeit: {{contractDuration}} Monate\nVerlängerung: {{contractExtension}} Monate\n\nViele Grüße\n{{tenantName}}',
      terminationTemplateMode:
        tenantInfo?.settings?.mail?.terminationTemplateMode || 'auto',
      terminationBcc: tenantInfo?.settings?.mail?.terminationBcc || '',
      testRecipient: currentUserData?.mail || '',
    }),
    [tenantInfo, currentUserData]
  );

  const { register, handleSubmit, control, reset, setValue, getValues } =
    useForm({ defaultValues });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const handleInsertToken = (token) => {
    if (editorRef.current) {
      editorRef.current.focus();
      document.execCommand('insertText', false, token);
      const next = editorRef.current.innerHTML || '';
      setValue('terminationTemplate', next, { shouldDirty: true });
      return;
    }
    const current = getValues('terminationTemplate') || '';
    setValue('terminationTemplate', current ? `${current} ${token}` : token, {
      shouldDirty: true,
    });
  };

  const onSubmit = async (data) => {
    const { testRecipient, ...mailSettings } = data;
    try {
      const result = await updateTenantMailSettings(mailSettings).unwrap();
      if (result?.type === 'success') {
        dispatch(
          setDialog({
            title: 'Einstellungen gespeichert',
            type: 'SUCCESS',
            data: { successText: result.message },
          })
        );
      }
    } catch (error) {
      dispatch(
        setDialog({
          title: 'Fehler',
          type: 'ERROR',
          data: {
            errorText:
              error?.data?.message ||
              'Einstellungen konnten nicht gespeichert werden.',
          },
        })
      );
    }
  };

  const handleSendTest = async () => {
    const recipient = (getValues('testRecipient') || '').trim();
    if (!recipient) {
      dispatch(
        setDialog({
          title: 'Hinweis',
          type: 'ERROR',
          data: { errorText: 'Bitte eine Test-E-Mail-Adresse angeben.' },
        })
      );
      return;
    }
    try {
      const result = await sendTenantTestMail({ recipient }).unwrap();
      if (result?.type === 'success') {
        dispatch(
          setDialog({
            title: 'Test-E-Mail gesendet',
            type: 'SUCCESS',
            data: { successText: result.message },
          })
        );
      }
    } catch (error) {
      dispatch(
        setDialog({
          title: 'Fehler',
          type: 'ERROR',
          data: {
            errorText:
              error?.data?.message || 'Test-E-Mail konnte nicht gesendet werden.',
          },
        })
      );
    }
  };

  if (isLoading) return null;

  return (
    <MailTemplatePageLayout
      title='Kündigungsbestätigung'
      description='Individualisieren Sie den Text der Kündigungsbestätigung, die nach Eingabe einer Kündigung an das Mitglied versendet wird.'
      subjectFieldName='terminationSubject'
      templateFieldName='terminationTemplate'
      bccFieldName='terminationBcc'
      templateType='termination'
      modeFieldName='terminationTemplateMode'
      currentHtmlTemplate={tenantInfo?.settings?.mail?.terminationHtmlTemplate}
      tokens={tokens}
      control={control}
      register={register}
      handleInsertToken={handleInsertToken}
      editorRef={editorRef}
      handleSubmit={handleSubmit}
      onSubmit={onSubmit}
      handleSendTest={handleSendTest}
      isSaving={isSaving}
      isTesting={isTesting}
      tenantName={tenantInfo?.tenantName}
      navigate={navigate}
    />
  );
};

export default MailTerminationTemplateSettings;
